import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import supabase from '@/lib/supabaseClient';

const PrivateRoute = ({ children }) => {
  const { user, loading, signOut, isRecoverySession } = useAuth();
  const location = useLocation();
  // Single source of truth for "have we checked approval for the CURRENT
  // user" -- checkedUserId is only set once a query for that exact user
  // has resolved, so a stale approved=false from a previous/null user can
  // never be mistaken for a real "not approved" result while a fresh
  // check is still in flight.
  const [approvalState, setApprovalState] = useState({ checkedUserId: null, approved: false });

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    supabase
      .from('user_profiles')
      .select('admin_approved')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        const approved = data?.admin_approved !== false; // fail open on error/missing row
        if (!approved) signOut();
        setApprovalState({ checkedUserId: user.id, approved });
      })
      .catch(() => {
        if (!cancelled) {
          setApprovalState({ checkedUserId: user.id, approved: true }); // fail open on network error
        }
      });

    return () => { cancelled = true; };
    // Keyed on user?.id (not the user object) so a same-user session-object
    // refresh during cold-start doesn't tear down and restart this query.
  }, [user?.id, signOut]);

  // Recovery session: user clicked a reset link — must set new password first
  if (isRecoverySession) {
    return <Navigate to="/reset-password" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Approval hasn't been checked for THIS user yet (covers cold-start,
  // user switching, and the render that happens before the effect above
  // has had a chance to run) -- keep showing the spinner rather than
  // treating the default approved=false as a real result.
  if (approvalState.checkedUserId !== user.id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!approvalState.approved) {
    return <Navigate to="/pending-approval" replace />;
  }

  return children;
};

export default PrivateRoute;
